import { useCurrentTenantConfig } from '../../../utils/tenants'
import { RoleSets } from '../../selfservice/roleAssignments/data'
import { useSupplierById } from '../../selfservice/supplier/data/hooks'
import {
  SupplierFeature,
  getFeatureForPermission,
} from '../constants/featurePermissionMap'

export type SupplierFeatures = {
  isOrderProcessActive: boolean
  isDeliveryForecastActive: boolean
  isEInvoiceActive: boolean
  isLoading: boolean
}

/**
 * Hook that returns which features are active for the currently selected
 * supplier (projectId). Uses the supplier's WebEdiSettings to determine
 * the IsProcessActive flags for each feature.
 */
export function useSupplierFeatures(): SupplierFeatures {
  const { projectId } = useCurrentTenantConfig()
  const { data: supplier, isLoading } = useSupplierById(projectId as string)

  return {
    isOrderProcessActive:
      supplier?.WebEdiSettings?.OrderProcessSettings?.IsProcessActive?.Value ??
      false,
    isDeliveryForecastActive:
      supplier?.WebEdiSettings?.DeliveryProcessForecastSettings?.IsProcessActive
        ?.Value ?? false,
    isEInvoiceActive:
      supplier?.WebEdiSettings?.EInvoiceProcessSettings?.IsProcessActive
        ?.Value ?? false,
    isLoading,
  }
}

/**
 * Returns whether a specific feature is active for the current supplier.
 */
export function isFeatureActive(
  features: SupplierFeatures,
  feature: SupplierFeature,
): boolean {
  switch (feature) {
    case 'orderProcess':
      return features.isOrderProcessActive
    case 'deliveryForecast':
      return features.isDeliveryForecastActive
    case 'eInvoice':
      return features.isEInvoiceActive
  }
}

/**
 * Filters role sets to exclude roles that only grant permissions
 * for deactivated features. Roles that have no feature-specific
 * permissions (e.g. admin roles) are always kept.
 *
 * A role is filtered out if ALL of its feature-related permissions
 * belong to deactivated features.
 */
export function filterRoleSetsByActiveFeatures(
  roleSets: RoleSets | undefined,
  features: SupplierFeatures,
): RoleSets | undefined {
  if (!roleSets) return roleSets

  return roleSets.filter(roleSet => {
    const featurePermissions = roleSet.permissions.filter(
      p => getFeatureForPermission(p.name) !== undefined,
    )

    // Roles without feature-specific permissions (admin, settings, etc.) are always shown
    if (featurePermissions.length === 0) return true

    // Keep the role if at least one of its feature permissions belongs to an active feature
    return featurePermissions.some(p => {
      const feature = getFeatureForPermission(p.name)
      return feature ? isFeatureActive(features, feature) : true
    })
  }) as RoleSets
}
